import { useContext, useEffect, useRef, useState } from "react";
import { DasboardContext } from "../../context/dashboard-context";
import Breadcrumb from "../../components/Breadcrumb/Breadcrumb";
import AlarmTable from "../../components/AlarmTable/AlarmTable";
import { FaFilter, FaCalendarAlt, FaSpinner } from "react-icons/fa";
import { BsFillAlarmFill } from "react-icons/bs";
import "./style.css";

const LIMIT = 20;

const Alarms = () => {
  const [tableData, setTableData] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [hasQueried, setHasQueried] = useState(false);
  const [skip, setSkip] = useState(0);
  const [userDevices, setUserDevices] = useState([]);
  const observerRef = useRef();

  const { bedData, userInfo, sidebarOpen } = useContext(DasboardContext);

  // Kullanıcıya atanmış cihazları al
  useEffect(() => {
    const fetchUserDevices = async () => {
      if (!userInfo?.username) return;
      
      try {
        const response = await fetch(`https://app.vivasmartcare.com/api/login/${userInfo.username}`);
        const data = await response.json();
        if (data[0]?.devices && data[0].devices.length > 0) {
          setUserDevices(data[0].devices);
        } else {
          setUserDevices([]);
        }
      } catch (error) {
        console.error("Kullanıcı cihazları alınırken hata:", error);
        setUserDevices([]);
      }
    };
    
    fetchUserDevices();
  }, [userInfo?.username]);

  const tableTD = [
    "Sıra No",
    "Saat Dilimi",
    "Süit Adı",
    "Oda Adı",
    "Olay Türü",
    "Cihaz Id",
    "Aranma Sayısı",
    "Teyit Durumu",
    "Açıklama",
    "Alarm Senaryosu",
    "İşlemi Yapan Kullanıcı",
    "Otomatik Yanıt",
  ];

  const getAlarmLogs = async (append = false) => {
    if (!startDate || !endDate) return;

    setLoading(true);
    try {
      const params = new URLSearchParams({
        start: startDate,
        end: endDate,
        skip: String(append ? skip : 0),
        limit: String(LIMIT),
        resolved: "true",
      });

      // Kurum bazlı filtreleme için parametreleri ekle
      if (userInfo?.company) {
        params.append("company", userInfo.company);
      }
      
      // Kullanıcıya atanmış cihazlar varsa ekle
      if (userDevices.length > 0) {
        params.append("userDevices", JSON.stringify(userDevices));
      }

      const res = await fetch(`https://app.vivasmartcare.com/api/alarm-logs?${params}`);
      const result = await res.json();

      const offset = append ? skip : 0;
      const withRowNo = (result.data || []).map((row, idx) => ({
        ...row,
        __rowNo: offset + idx + 1,
      }));

      if (append) {
        setTableData((prev) => [...prev, ...withRowNo]);
        setSkip((prev) => prev + result.data.length);
      } else {
        setTableData(withRowNo);
        setSkip(result.data.length);
      }

      setHasMore(result.hasMore);
      setHasQueried(true);
    } catch (error) {
      console.error("Veri getirilirken bir hata oluştu:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = () => {
    setTableData([]);
    setSkip(0);
    setHasQueried(false);
    getAlarmLogs(false);
  };

  useEffect(() => {
    if (loading) return;
    if (!hasMore) return;
    if (!observerRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          getAlarmLogs(true);
        }
      },
      { threshold: 1.0 }
    );

    observer.observe(observerRef.current);

    return () => observer.disconnect();
  }, [loading, hasMore, startDate, endDate]);

  return (
    <>
      <Breadcrumb />
      <div
        className={`alarms-wrapper component-wrapper ${
          sidebarOpen ? "" : "close"
        }`}
      >
        <div className="alarms-header">
          <div className="header-content">
            <BsFillAlarmFill className="header-icon" />
            <h2>Alarm Yönetimi</h2>
          </div>
          <p className="header-subtitle">
            Teyit edilmiş alarmları görüntüleyin ve filtreleyin
          </p>
        </div>

        <div className="date-filter-card">
          <div className="filter-header">
            <FaFilter className="filter-icon" />
            <span>Tarih Filtresi</span>
          </div>
        <div className="date-filter">
            <div className="date-input-group">
          <label>
                <FaCalendarAlt className="input-icon" />
                Başlangıç Tarihi
              </label>
            <input
              type="datetime-local"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
                className="date-input"
            />
            </div>
            <div className="date-input-group">
              <label>
                <FaCalendarAlt className="input-icon" />
                Bitiş Tarihi
          </label>
            <input
              type="datetime-local"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
                className="date-input"
                min={startDate}
            />
            </div>
          <button
              className="filter-button"
            onClick={handleFilter}
            disabled={!startDate || !endDate || loading}
          >
              {loading && !tableData.length ? (
                <>
                  <FaSpinner className="spinner" /> Yükleniyor...
                </>
              ) : (
                <>
                  <FaFilter /> Filtrele
                </>
              )}
          </button>
          </div>
        </div>

        {loading && tableData.length === 0 && (
          <div className="loading-container">
            <FaSpinner className="loading-spinner" />
            <p>Veriler yükleniyor...</p>
          </div>
        )}

        {tableData.length > 0 ? (
          <>
            <div className="table-container">
            <AlarmTable
              key={`${startDate}-${endDate}`}
              tableTD={tableTD}
              tableData={tableData}
              bedData={bedData}
              userInfo={userInfo}
            />
            </div>

            <div ref={observerRef} className="load-more-container">
              {loading && hasMore && (
                <div className="loading-more">
                  <FaSpinner className="spinner-small" />
                  <span>Daha fazla veri yükleniyor...</span>
                </div>
              )}
              {!loading && !hasMore && hasQueried && (
                <div className="load-complete">
                  <span>✓ Tüm veriler yüklendi</span>
                </div>
              )}
            </div>
          </>
        ) : (
          hasQueried &&
          !loading &&
          startDate &&
          endDate && (
            <div className="empty-state">
              <BsFillAlarmFill className="empty-icon" />
              <h3>Alarm Bulunamadı</h3>
              <p>
                Bu tarih aralığında teyit edilmiş alarm bulunamadı.
                <br />
                Lütfen farklı bir tarih aralığı seçin.
            </p>
            </div>
          )
        )}

        {!startDate || !endDate ? (
          <div className="info-banner">
            <FaCalendarAlt />
            <span>Lütfen başlangıç ve bitiş tarihi seçiniz</span>
          </div>
        ) : null}
      </div>
    </>
  );
};

export default Alarms;
